import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearch } from 'wouter';
import {
  ChevronDown, ChevronRight, ShieldCheck, AlertTriangle, CheckCircle2,
  ClipboardCheck, Lock, Layers, FileText, GitBranch, Mic, Sparkles,
  ArrowUpRight, Info,
} from 'lucide-react';
import { usePortalStore } from '@/store/portal-store.js';
import type { PortalMode } from '@/store/portal-store.js';
import { getPortalRequestInit } from '@edison/api-client-react';
import { SkeletonCard } from '@/components/ui/skeleton.js';
import { InlineError } from '@/components/ui/inline-error.js';
import { PortalArtefactPanel } from '@/components/ui/portal-artefact-panel.js';
import type { ArtefactRef } from '@/components/ui/portal-artefact-panel.js';

// ── Types ─────────────────────────────────────────────────────────────────────

interface TfaPanelistSummary {
  userId: string;
  name: string | null;
  hasCompetenceProfile: boolean;
  profilePinned: boolean;
}

interface TfaEligibilitySection {
  stage: string;
  locked: boolean;
  problemStatement: string | null;
  cotsSweepNotes: string | null;
  determination: { determination: string; notes: string | null } | null;
  panelists: TfaPanelistSummary[];
  deliberationCriteria: Array<{
    questionKey: string;
    panelPositive: number;
    panelNegative: number;
    comments: string[];
  }>;
}

interface ActivityCompetenceSummary {
  assessorUserId: string;
  qualifications: string | null;
  relevantExpertise: string | null;
  roleOnActivity: string;
  uncertaintyBasis: string;
  confirmedAt: string | null;
}

interface CoreActivityReport {
  id: string;
  name: string;
  description: string | null;
  ipOwnership: string;
  competenceRecords: ActivityCompetenceSummary[];
  supportingActivities: Array<{ id: string; name: string; ipOwnership: string }>;
}

interface DocumentationAppendix {
  projectPlansAndPrds: ArtefactRef[];
  githubArtefacts: ArtefactRef[];
  testingAndBenchmarking: ArtefactRef[];
  experimentation: ArtefactRef[];
  aiModelEvaluation: ArtefactRef[];
  userFeedback: ArtefactRef[];
  other: ArtefactRef[];
}

interface ProjectEligibilityReport {
  projectId: string;
  projectName: string;
  incomeYear: string | null;
  initiativeId: string | null;
  tfa: TfaEligibilitySection | null;
  coreActivities: CoreActivityReport[];
  documentationAppendix: DocumentationAppendix;
  generatedAt: string;
}

interface PortfolioIndex {
  ungrouped: ProjectEligibilityReport[];
  byInitiative: Array<{ initiativeId: string; projects: ProjectEligibilityReport[] }>;
}

interface DefensibilityAcknowledgement {
  projectId: string;
  projectName: string;
  criterion: string;
  score: string;
  text: string;
}

// ── Rating derivation (mirrored in portal-ask.tsx) ────────────────────────────

type Rating = 'strong' | 'moderate' | 'weak' | 'gap';

const RATING_BADGE: Record<Rating, string> = {
  strong:   'bg-green-900/60 text-green-300 border border-green-800/50',
  moderate: 'bg-amber-900/60 text-amber-300 border border-amber-800/50',
  weak:     'bg-orange-900/60 text-orange-300 border border-orange-800/50',
  gap:      'bg-red-900/60 text-red-300 border border-red-800/50',
};

const RATING_LABEL: Record<Rating, string> = {
  strong: 'Strong', moderate: 'Moderate', weak: 'Weak', gap: 'Gap',
};

interface Ratings {
  hypothesis: Rating;
  systematic: Rating;
  newKnowledge: Rating;
  records: Rating;
}

function deriveRatings(project: ProjectEligibilityReport): Ratings {
  const d = project.documentationAppendix;
  const hasExperimental = (d.testingAndBenchmarking.length + d.experimentation.length) > 0;
  const totalCount = d.githubArtefacts.length + d.projectPlansAndPrds.length +
    d.testingAndBenchmarking.length + d.experimentation.length +
    d.aiModelEvaluation.length + d.userFeedback.length + d.other.length;

  const hypothesis: Rating = project.tfa?.locked && project.tfa.problemStatement ? 'strong' :
    project.tfa?.locked ? 'moderate' : 'gap';

  const systematic: Rating = project.coreActivities.length > 0 && hasExperimental ? 'strong' :
    project.coreActivities.length > 0 ? 'moderate' : 'gap';

  const newKnowledge: Rating = project.tfa?.determination?.determination === 'uncertain' ? 'strong' :
    project.tfa?.determination?.determination === 'partially_uncertain' ? 'moderate' :
    project.tfa?.locked ? 'weak' : 'gap';

  const records: Rating = totalCount >= 10 ? 'strong' :
    totalCount >= 3 ? 'moderate' :
    totalCount > 0 ? 'weak' : 'gap';

  return { hypothesis, systematic, newKnowledge, records };
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function determinationBadge(det?: string | null): { label: string; cls: string } {
  if (!det) return { label: 'TFA In Progress', cls: 'bg-gray-700 text-gray-300' };
  if (det === 'uncertain') return { label: 'Uncertain — Eligible', cls: 'bg-green-900/60 text-green-300' };
  if (det === 'partially_uncertain') return { label: 'Partially Uncertain', cls: 'bg-amber-900/60 text-amber-300' };
  if (det === 'certain') return { label: 'Certain — Ineligible', cls: 'bg-red-900/60 text-red-300' };
  return { label: det, cls: 'bg-gray-700 text-gray-300' };
}

function totalArtefacts(appendix: DocumentationAppendix): number {
  return appendix.githubArtefacts.length + appendix.projectPlansAndPrds.length +
    appendix.testingAndBenchmarking.length + appendix.experimentation.length +
    appendix.aiModelEvaluation.length + appendix.userFeedback.length + appendix.other.length;
}

// ── Artefact type helpers ──────────────────────────────────────────────────────

function artefactTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    github_commit: 'GitHub commit',
    github_pr: 'GitHub pull request',
    github_issue: 'GitHub issue',
    meeting_transcript: 'Meeting transcript',
    llm_session: 'AI session',
    prd_document: 'PRD document',
    manual_note: 'Manual note',
    file_upload: 'File upload',
    abandoned_work: 'Abandoned work',
    market_search: 'Market search',
  };
  return labels[type] ?? type.replace(/_/g, ' ');
}

function ArtefactTypeIcon({ type, className }: { type: string; className?: string }) {
  if (type.startsWith('github_')) return <GitBranch className={className} aria-hidden="true" />;
  if (type === 'meeting_transcript') return <Mic className={className} aria-hidden="true" />;
  if (type === 'llm_session') return <Sparkles className={className} aria-hidden="true" />;
  return <FileText className={className} aria-hidden="true" />;
}

// ── Artefact chip (compact, used in evidence appendix) ──────────────────────────

/** Truncates a citation quote to ~120 chars for a compact one-line preview. */
function truncateQuote(quote: string, maxLength: number): string {
  if (quote.length <= maxLength) return quote;
  return `${quote.slice(0, maxLength).trimEnd()}…`;
}

function ArtefactChip({ artefact, onOpen }: { artefact: ArtefactRef; onOpen: (a: ArtefactRef) => void }) {
  return (
    <button
      onClick={() => onOpen(artefact)}
      className="flex items-center gap-1.5 rounded-md border border-white/10 bg-white/5 px-2.5 py-1.5 text-left hover:border-blue-500/50 hover:bg-blue-500/10 transition-colors group"
    >
      <ArtefactTypeIcon type={artefact.type} className="h-3 w-3 text-white/30 group-hover:text-blue-400 shrink-0" />
      <span className="min-w-0">
        <span className="block text-xs text-white/60 group-hover:text-blue-300 leading-snug line-clamp-1">
          {artefact.title}
        </span>
        {artefact.citation && (
          <span className="block text-[11px] text-white/45 italic line-clamp-1">
            &ldquo;{truncateQuote(artefact.citation.quote, 120)}&rdquo;
          </span>
        )}
      </span>
    </button>
  );
}

// ── Artefact row (drillable, used inside criterion cards) ─────────────────────

function ArtefactRow({ artefact, onOpen }: { artefact: ArtefactRef; onOpen: (a: ArtefactRef) => void }) {
  return (
    <button
      onClick={() => onOpen(artefact)}
      className="w-full flex items-center gap-3 py-2.5 px-0 border-t border-white/8 text-left hover:opacity-75 transition-opacity group"
    >
      <div className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[7px] bg-white/10">
        <ArtefactTypeIcon type={artefact.type} className="h-3.5 w-3.5 text-white/50" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[12.5px] font-medium text-white/85 truncate leading-snug">{artefact.title}</p>
        <p className="text-[11px] text-white/40">{artefactTypeLabel(artefact.type)}</p>
        {artefact.citation && (
          <p className="text-[11px] text-white/45 italic line-clamp-1 mt-0.5">
            &ldquo;{truncateQuote(artefact.citation.quote, 120)}&rdquo;
          </p>
        )}
      </div>
      <ArrowUpRight className="h-3.5 w-3.5 text-white/30 group-hover:text-white/60 shrink-0 transition-colors" aria-hidden="true" />
    </button>
  );
}

// ── Gateway criterion card ─────────────────────────────────────────────────────

interface CriterionDef {
  key: string;
  label: string;
  subtitle: string;
  rating: Rating;
  artefactCount: number;
  why: string;
  artefacts: ArtefactRef[];
}

function buildCriteria(project: ProjectEligibilityReport): CriterionDef[] {
  const d = project.documentationAppendix;
  const ratings = deriveRatings(project);

  // Full lists — the true evidence count. The card shows a preview with a "show all" toggle, so the
  // count is never capped (previously .slice(0,5) here undercounted "backed by N artefacts").
  const hypothesisArtefacts = [...d.projectPlansAndPrds];
  const systematicArtefacts = [...d.testingAndBenchmarking, ...d.experimentation];
  const newKnowledgeArtefacts = [...d.aiModelEvaluation, ...d.experimentation];
  const recordsArtefacts = [...d.githubArtefacts];

  const hypothesisWhyMap: Record<Rating, string> = {
    strong: `A dated problem statement is locked in the TFA panel record. The technical question was formally framed before the work began and is panel-signed.`,
    moderate: `A TFA panel has been completed and locked, but the specific problem statement was not recorded or is thin. The hypothesis exists but lacks formal documentation.`,
    weak: `TFA assessment exists but is not yet locked, or the problem framing is incomplete.`,
    gap: `No TFA assessment has been locked for this project. The technical hypothesis is not formally documented.`,
  };
  const hypothesisWhy = hypothesisWhyMap[ratings.hypothesis];

  const n = project.coreActivities.length;
  const actWord = n === 1 ? 'activity' : 'activities';
  const systematicWhyMap: Record<Rating, string> = {
    strong: `${n} core ${actWord} are backed by experimental evidence including benchmarks, iterative tests, and documented trials.`,
    moderate: `${n} core ${actWord} ${n === 1 ? 'is' : 'are'} defined but experimental documentation (benchmarks, test logs) is limited.`,
    weak: `Core activities are partially defined but systematic experimental evidence is sparse.`,
    gap: `No core R&D activities have been defined for this project.`,
  };
  const systematicWhy = systematicWhyMap[ratings.systematic];

  const newKnowledgeWhyMap: Record<Rating, string> = {
    strong: `The TFA panel determined the project involves genuine technical uncertainty (s355-25). The outcome — whether the work succeeds or fails — generates new technical knowledge.`,
    moderate: `The TFA panel found partial uncertainty. Some aspects of the work are genuinely uncertain; others may be capable of resolution without novel R&D.`,
    weak: `TFA is locked but the determination record is thin or the outcome wasn't decisively uncertain.`,
    gap: `No TFA determination has been recorded. New knowledge generation cannot be confirmed.`,
  };
  const newKnowledgeWhy = newKnowledgeWhyMap[ratings.newKnowledge];

  const total = totalArtefacts(d);
  const recordsWhyMap: Record<Rating, string> = {
    strong: `${total} contemporaneous artefacts are on record — GitHub commits, transcripts, PRD documents, and test logs captured as work progressed.`,
    moderate: `${total} artefacts are present, covering some work periods. Records are present but not comprehensive across the full project timeline.`,
    weak: `${total} artefact${total === 1 ? '' : 's'} recorded — minimal contemporaneous documentation.`,
    gap: `No evidence artefacts are linked to this project.`,
  };
  const recordsWhy = recordsWhyMap[ratings.records];

  return [
    {
      key: 'hypothesis',
      label: 'Hypothesis',
      subtitle: 'Is the technical question documented before work began?',
      rating: ratings.hypothesis,
      artefactCount: hypothesisArtefacts.length,
      why: hypothesisWhy,
      artefacts: hypothesisArtefacts,
    },
    {
      key: 'systematic',
      label: 'Systematic experiment',
      subtitle: 'Is there evidence of structured experimentation?',
      rating: ratings.systematic,
      artefactCount: systematicArtefacts.length,
      why: systematicWhy,
      artefacts: systematicArtefacts,
    },
    {
      key: 'newKnowledge',
      label: 'New knowledge',
      subtitle: 'Does the work generate new technical understanding?',
      rating: ratings.newKnowledge,
      artefactCount: newKnowledgeArtefacts.length,
      why: newKnowledgeWhy,
      artefacts: newKnowledgeArtefacts,
    },
    {
      key: 'records',
      label: 'Contemporaneous records',
      subtitle: 'Are the records captured as work progressed?',
      rating: ratings.records,
      artefactCount: recordsArtefacts.length,
      why: recordsWhy,
      artefacts: recordsArtefacts,
    },
  ];
}

function CriterionCard({
  criterion,
  onOpenArtefact,
}: {
  criterion: CriterionDef;
  onOpenArtefact: (a: ArtefactRef) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const PREVIEW = 5;
  const shownArtefacts = expanded ? criterion.artefacts : criterion.artefacts.slice(0, PREVIEW);
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-3">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className="text-sm font-semibold text-white">{criterion.label}</span>
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${RATING_BADGE[criterion.rating]}`}>
              {RATING_LABEL[criterion.rating]}
            </span>
            {criterion.artefactCount > 0 && (
              <span className="text-xs text-white/30">backed by {criterion.artefactCount} artefact{criterion.artefactCount !== 1 ? 's' : ''}</span>
            )}
          </div>
          <p className="text-[11px] text-white/40">{criterion.subtitle}</p>
        </div>
      </div>

      <div>
        <p className="text-[10px] text-white/30 uppercase tracking-wider mb-1">
          Why {RATING_LABEL[criterion.rating].toLowerCase()}:
        </p>
        <p className="text-xs text-white/70 leading-relaxed">{criterion.why}</p>
      </div>

      {criterion.artefacts.length > 0 && (
        <div className="-mx-4 px-4">
          {shownArtefacts.map((a) => (
            <ArtefactRow key={a.id} artefact={a} onOpen={onOpenArtefact} />
          ))}
          {criterion.artefacts.length > PREVIEW && (
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              className="mt-1.5 text-xs text-indigo-300 hover:text-indigo-200 transition-colors"
            >
              {expanded ? 'Show fewer' : `Show all ${criterion.artefacts.length} artefacts`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Project detail (expanded 4-criteria view) ─────────────────────────────────

function ProjectDetail({
  project,
  onOpenArtefact,
}: {
  project: ProjectEligibilityReport;
  onOpenArtefact: (a: ArtefactRef) => void;
}) {
  const criteria = buildCriteria(project);
  const det = determinationBadge(project.tfa?.determination?.determination);
  const ipRisk = project.coreActivities.some(
    (a) => a.ipOwnership === 'client' || a.ipOwnership === 'undetermined',
  );

  return (
    <div className="space-y-4">
      {/* Drillable info notice */}
      <div className="flex items-center gap-2.5 rounded-lg border border-blue-500/20 bg-blue-500/10 px-3.5 py-2.5">
        <Info className="h-3.5 w-3.5 text-blue-400 shrink-0" aria-hidden="true" />
        <p className="text-xs text-blue-300/80">
          Every rating below is drillable to its source. Open any artefact to inspect it.
        </p>
      </div>

      {/* 4 gateway criteria */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <p className="text-xs font-semibold text-white/70 uppercase tracking-wider">
            The four gateway criteria · s355-20
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {criteria.map((c) => (
            <CriterionCard key={c.key} criterion={c} onOpenArtefact={onOpenArtefact} />
          ))}
        </div>
      </div>

      {/* TFA determination */}
      {project.tfa && (
        <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <ShieldCheck className="h-4 w-4 text-blue-400 shrink-0" aria-hidden="true" />
            <span className="text-sm font-semibold text-white">TFA Panel Determination</span>
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${det.cls}`}>
              {det.label}
            </span>
            {project.tfa.locked && (
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-blue-900/50 text-blue-300">
                <Lock className="h-2.5 w-2.5" aria-hidden="true" />
                Locked
              </span>
            )}
          </div>

          {project.tfa.problemStatement && (
            <div>
              <p className="text-[10px] text-white/30 uppercase tracking-wider mb-1">Problem statement</p>
              <p className="text-xs text-white/70 leading-relaxed">{project.tfa.problemStatement}</p>
            </div>
          )}

          {project.tfa.cotsSweepNotes && (
            <div>
              <p className="text-[10px] text-white/30 uppercase tracking-wider mb-1">COTS evaluation</p>
              <p className="text-xs text-white/50 leading-relaxed">{project.tfa.cotsSweepNotes}</p>
            </div>
          )}

          {project.tfa.determination?.notes && (
            <div>
              <p className="text-[10px] text-white/30 uppercase tracking-wider mb-1">Panel notes</p>
              <p className="text-xs text-white/60 italic">"{project.tfa.determination.notes}"</p>
            </div>
          )}

          {project.tfa.panelists.length > 0 && (
            <div>
              <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">Panel members</p>
              <div className="flex flex-wrap gap-2">
                {project.tfa.panelists.map((p) => (
                  <span
                    key={p.userId}
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs ${
                      p.hasCompetenceProfile
                        ? 'bg-green-900/40 text-green-300'
                        : 'bg-gray-800 text-white/40'
                    }`}
                  >
                    {p.hasCompetenceProfile
                      ? <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
                      : <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                    }
                    {p.name ?? 'Unknown panelist'}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Deliberation criteria as progression table */}
          {project.tfa.deliberationCriteria.length > 0 && (
            <div>
              <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">Panel deliberation scores</p>
              <div className="space-y-1.5">
                {project.tfa.deliberationCriteria.map((dc) => {
                  const total = dc.panelPositive + dc.panelNegative;
                  const pct = total > 0 ? Math.round((dc.panelPositive / total) * 100) : 0;
                  const outcome = pct >= 70 ? 'Met' : pct >= 40 ? 'Partial' : 'Failed';
                  const outcomeCls = outcome === 'Met' ? 'bg-green-900/60 text-green-300' :
                    outcome === 'Partial' ? 'bg-amber-900/60 text-amber-300' :
                    'bg-red-900/60 text-red-300';
                  return (
                    <div key={dc.questionKey} className="flex items-center gap-3 text-xs">
                      <span className="text-white/50 flex-1 truncate">{dc.questionKey.replace(/_/g, ' ')}</span>
                      <span className="text-white/40">{dc.panelPositive}/{total}</span>
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${outcomeCls}`}>
                        {outcome}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Core activities */}
      {project.coreActivities.length > 0 && (
        <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-3">
          <p className="text-xs font-semibold text-white/70 uppercase tracking-wider">
            Core R&D activities ({project.coreActivities.length})
          </p>
          <div className="divide-y divide-white/5 -mx-4">
            {project.coreActivities.map((act) => (
              <div key={act.id} className="px-4 py-2.5 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-400 shrink-0" aria-hidden="true" />
                  <span className="text-sm text-white/90">{act.name}</span>
                  {act.ipOwnership === 'client' && (
                    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-red-900/60 text-red-300">
                      <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                      Client IP — HIGH RISK
                    </span>
                  )}
                  {act.ipOwnership === 'undetermined' && (
                    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-amber-900/60 text-amber-300">
                      IP Undetermined
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
          {ipRisk && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-400 mt-0.5 shrink-0" aria-hidden="true" />
              <p className="text-xs text-amber-300">
                One or more activities have IP ownership concerns. Client-IP activities are excluded from the R&D tax offset.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Full documentation appendix */}
      {totalArtefacts(project.documentationAppendix) > 0 && (
        <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-3">
          <p className="text-xs font-semibold text-white/40 uppercase tracking-wider">
            Full evidence appendix ({totalArtefacts(project.documentationAppendix)} artefacts)
          </p>
          {([
            ['GitHub', project.documentationAppendix.githubArtefacts],
            ['Plans & PRDs', project.documentationAppendix.projectPlansAndPrds],
            ['Testing & Benchmarking', project.documentationAppendix.testingAndBenchmarking],
            ['Experimentation', project.documentationAppendix.experimentation],
            ['AI Evaluations', project.documentationAppendix.aiModelEvaluation],
            ['User Feedback', project.documentationAppendix.userFeedback],
            ['Other', project.documentationAppendix.other],
          ] as [string, ArtefactRef[]][]).filter(([, refs]) => refs.length > 0).map(([label, refs]) => (
            <div key={label}>
              <p className="text-[10px] text-white/25 uppercase tracking-wider mb-1.5">{label}</p>
              <div className="flex flex-wrap gap-1.5">
                {refs.map((a) => <ArtefactChip key={a.id} artefact={a} onOpen={onOpenArtefact} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Project card (compact, portfolio view) ─────────────────────────────────────

function ProjectCard({
  project,
  autoExpand,
  onOpenArtefact,
}: {
  project: ProjectEligibilityReport;
  autoExpand: boolean;
  onOpenArtefact: (a: ArtefactRef) => void;
}) {
  const [expanded, setExpanded] = useState(autoExpand);
  const cardRef = useRef<HTMLDivElement>(null);
  const ratings = deriveRatings(project);
  const gapCount = [ratings.hypothesis, ratings.systematic, ratings.newKnowledge, ratings.records].filter((r) => r === 'gap').length;
  const isReady = gapCount === 0;

  useEffect(() => {
    if (autoExpand && cardRef.current) {
      setTimeout(() => cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  }, [autoExpand]);

  return (
    <div ref={cardRef} className="rounded-xl border border-white/10 bg-white/5 overflow-hidden">
      <button
        className="w-full flex items-start gap-4 p-4 text-left hover:bg-white/5 transition-colors"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <ShieldCheck className="h-4.5 w-4.5 text-blue-400 shrink-0 mt-0.5" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <span className="text-sm font-semibold text-white">{project.projectName}</span>
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
              isReady ? 'bg-green-900/60 text-green-300' : 'bg-amber-900/60 text-amber-300'
            }`}>
              {isReady ? 'Audit-ready' : `${gapCount} gap${gapCount > 1 ? 's' : ''}`}
            </span>
            {project.tfa?.locked && (
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium bg-blue-900/50 text-blue-300">
                <Lock className="h-2.5 w-2.5" aria-hidden="true" />
                TFA Locked
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5 items-center">
            {([
              ['Hypothesis', ratings.hypothesis],
              ['Systematic', ratings.systematic],
              ['New knowledge', ratings.newKnowledge],
              ['Records', ratings.records],
            ] as [string, Rating][]).map(([label, rating]) => (
              <span key={label} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${RATING_BADGE[rating]}`}>
                {RATING_LABEL[rating]} · {label}
              </span>
            ))}
            <span className="text-xs text-white/30">{totalArtefacts(project.documentationAppendix)} artefacts</span>
            {project.incomeYear && (
              <span className="text-xs text-white/20">{project.incomeYear}</span>
            )}
          </div>
        </div>
        {expanded
          ? <ChevronDown className="h-4 w-4 text-white/40 shrink-0 mt-0.5" aria-hidden="true" />
          : <ChevronRight className="h-4 w-4 text-white/40 shrink-0 mt-0.5" aria-hidden="true" />
        }
      </button>

      {expanded && (
        <div className="border-t border-white/10 p-4">
          <ProjectDetail project={project} onOpenArtefact={onOpenArtefact} />
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function PortalEligibilityPage() {
  const token = usePortalStore((s) => s.token);
  const mode = usePortalStore((s) => s.mode);
  const search = useSearch();
  const searchParams = new URLSearchParams(search);
  const autoExpandProjectId = searchParams.get('project');

  const [portfolio, setPortfolio] = useState<PortfolioIndex | null>(null);
  const [acknowledgements, setAcknowledgements] = useState<DefensibilityAcknowledgement[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedArtefact, setSelectedArtefact] = useState<ArtefactRef | null>(null);

  const fetchData = useCallback(() => {
    if (!token) { setIsLoading(false); return; }
    setIsLoading(true);
    setError(null);
    getPortalRequestInit(token, mode as PortalMode)
      .then(({ base, headers }) =>
        Promise.all([
          fetch(`${base}/eligibility`, { headers }).then((r) => r.json()),
          fetch(`${base}/claim`, { headers }).then((r) => r.json()),
        ])
      )
      .then(([eligBody, claimBody]: [
        { data?: PortfolioIndex; error?: { message: string } },
        { data?: { defensibilityAcknowledgements?: DefensibilityAcknowledgement[] | null }; error?: { message: string } },
      ]) => {
        if (eligBody.error) throw new Error(eligBody.error.message);
        setPortfolio(eligBody.data ?? null);
        setAcknowledgements(claimBody.data?.defensibilityAcknowledgements ?? null);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setIsLoading(false));
  }, [token, mode]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} />)}
      </div>
    );
  }

  if (error) {
    return <InlineError message="Failed to load eligibility report." onRetry={fetchData} />;
  }

  const allProjects = portfolio
    ? [...(portfolio.ungrouped ?? []), ...(portfolio.byInitiative ?? []).flatMap((g) => g.projects)]
    : [];

  if (!portfolio || allProjects.length === 0) {
    return (
      <div className="text-center py-12 text-white/40 text-sm">
        No eligibility evidence assembled yet.
      </div>
    );
  }

  const totalCoreActivities = allProjects.reduce((n, p) => n + p.coreActivities.length, 0);
  const totalArtefactCount = allProjects.reduce((n, p) => n + totalArtefacts(p.documentationAppendix), 0);
  const ipRiskCount = allProjects.filter((p) =>
    p.coreActivities.some((a) => a.ipOwnership === 'client' || a.ipOwnership === 'undetermined'),
  ).length;

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="h-5 w-5 text-blue-400" aria-hidden="true" />
            <h1 className="text-lg font-semibold text-white">Eligibility Evidence</h1>
          </div>
          <p className="text-xs text-white/40 mb-4">
            Four gateway criteria assessed per project — as required under s355-20 ITAA 1997
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'R&D projects', value: allProjects.length.toString() },
              { label: 'Core activities', value: totalCoreActivities.toString() },
              { label: 'Evidence artefacts', value: totalArtefactCount.toString() },
              {
                label: 'IP risk flags',
                value: ipRiskCount.toString(),
                highlight: ipRiskCount > 0,
              },
            ].map(({ label, value, highlight }) => (
              <div
                key={label}
                className={`rounded-lg border px-4 py-3 ${
                  highlight ? 'border-amber-500/30 bg-amber-500/10' : 'border-white/10 bg-white/5'
                }`}
              >
                <p className={`text-2xl font-semibold ${highlight ? 'text-amber-300' : 'text-white'}`}>{value}</p>
                <p className={`text-xs mt-0.5 ${highlight ? 'text-amber-400/70' : 'text-white/40'}`}>{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Ungrouped projects */}
        {portfolio.ungrouped.length > 0 && (
          <div className="space-y-3">
            {portfolio.ungrouped.map((project) => (
              <ProjectCard
                key={project.projectId}
                project={project}
                autoExpand={project.projectId === autoExpandProjectId}
                onOpenArtefact={setSelectedArtefact}
              />
            ))}
          </div>
        )}

        {/* Initiative-grouped projects */}
        {portfolio.byInitiative.map((group) => (
          <div key={group.initiativeId} className="space-y-2">
            <div className="flex items-center gap-2 px-1">
              <Layers className="h-3.5 w-3.5 text-white/30" aria-hidden="true" />
              <p className="text-xs font-medium text-white/40 uppercase tracking-wider">Initiative</p>
            </div>
            <div className="space-y-3">
              {group.projects.map((project) => (
                <ProjectCard
                  key={project.projectId}
                  project={project}
                  autoExpand={project.projectId === autoExpandProjectId}
                  onOpenArtefact={setSelectedArtefact}
                />
              ))}
            </div>
          </div>
        ))}

        {/* Defensibility acknowledgements */}
        {acknowledgements && acknowledgements.length > 0 && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-5">
            <div className="flex items-center gap-2 mb-4">
              <ClipboardCheck className="h-4 w-4 text-amber-400" aria-hidden="true" />
              <h2 className="text-sm font-semibold text-amber-300">Defensibility Acknowledgements</h2>
            </div>
            <p className="text-xs text-white/50 mb-4">
              Written acknowledgements provided for criteria rated Weak or Gap.
            </p>
            <div className="space-y-3">
              {acknowledgements.map((ack, i) => (
                <div key={i} className="rounded-lg border border-white/10 bg-white/5 p-4">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="text-xs font-medium text-white">{ack.projectName}</span>
                    <span className="text-white/30">·</span>
                    <span className="text-xs text-white/70">{ack.criterion.replace(/_/g, ' ')}</span>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                      ack.score === 'gap' ? 'bg-red-900/60 text-red-300' : 'bg-amber-900/60 text-amber-300'
                    }`}>
                      {ack.score.charAt(0).toUpperCase() + ack.score.slice(1)}
                    </span>
                  </div>
                  <p className="text-xs text-white/80 leading-relaxed">{ack.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {selectedArtefact && token && (
        <PortalArtefactPanel
          artefact={selectedArtefact}
          citation={selectedArtefact?.citation}
          token={token}
          mode={mode}
          onClose={() => setSelectedArtefact(null)}
        />
      )}
    </>
  );
}
